import React, { memo, useMemo, useCallback, useEffect, useState } from 'react';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { SequenceDrillBlockSettings } from './SequenceDrillBlockSettings';
import { Row, Col, Container } from 'reactstrap';
import { of } from 'rxjs';
import { useBloc } from '@core/utils/bloc';
import { SequenceDrillBlockBloc } from './SequenceDrillBlockBloc';
import { Button } from '@core/components/Button';
import classNames from 'classnames';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { R } from '@core/utils/r';
import { useDidMount } from '@core/utils/hooks/react';
import { SortableList } from '@modules/common/Sortable';
import { SortableElement, SortEnd } from 'react-sortable-hoc';
import { HTMLService } from '@business/services';
import './index.scss';

function defineSequenceItemForm() {
  return formGroup({
    item: formControl<string>({
      placeholder: 'Item',
      initialValue: '',
    })
  });
}

function defineSequenceForm() {
  return formGroup({
    description: formControl<string>({
      placeholder: 'Instructions',
      initialValue: '',
    }),
    items: formArray(() => defineSequenceItemForm()),
  })
}

function defineSequenceDrillBlockForm() {
  return formGroup({
    sequence: formArray(() => defineSequenceForm()),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type SequenceItemForm = ReturnType<typeof defineSequenceItemForm>;
export type SequenceItemData = FormDataType<typeof defineSequenceItemForm>;

export type SequenceForm = ReturnType<typeof defineSequenceForm>;
export type SequenceData = FormDataType<typeof defineSequenceForm>;

export type SequenceDrillWidgetFormData = FormDataType<typeof defineSequenceDrillBlockForm>;
export type SequenceDrillWidgetForm = ReturnType<typeof defineSequenceDrillBlockForm>;

export enum SequenceDrillBlockVariation {
  sequenceDrill = 'sequenceDrill',
}

export const SequenceDrillBlock = memo((props: CourseBuilder.BlockProps<SequenceDrillBlockVariation>) => {
  const { variation, preview, lessonId } = props;
  const isEditable = props.editable;
  const editable = useMemo(() => !!isEditable || !!preview, [isEditable, preview]);
  const bloc = useBloc(SequenceDrillBlockBloc, lessonId);
  const form = useMemo(() => defineSequenceDrillBlockForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const sequence = useObservable(form.controls.sequence.items$, []);
  const sequenceItems = useMemo(() => (sequence), [sequence]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isDrillCompleted, setIsDrillCompleted] = useState(false);
  const activeSequence = useMemo(() => R.get(sequenceItems, activeIndex), [activeIndex, sequenceItems]);
  const [wrongAnswer, setWrongAnswer] = useState(false);

  useDidMount(() => {
    if (props.preview && !formValue.sequence.length) {
      form.patchValue({
        sequence: [
          {
            description: 'Instructions',
            items: [{ item: 'Item 1' }, { item: 'Item 2' }, { item: 'Item 3' }],
          },
        ],
      });
    }
  });

  const onResetDrill = useCallback(() => {
    setActiveIndex(0);
    setIsDrillCompleted(false);
  }, [activeIndex, isDrillCompleted]);

  useEffect(() => {
    if (activeIndex === -1 && sequence.length > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, sequence.length]);

  const onNext = useCallback(
    (shouldMove: boolean) => {
      if (!activeSequence) return;
      if (!shouldMove) { return setWrongAnswer(true); }
      else { setWrongAnswer(false); }
      if (activeIndex + 1 == sequence.length) return setIsDrillCompleted(true);
      if (props.editable || props.preview || R.isEmpty(lessonId)) return shouldMove && setActiveIndex(activeIndex + 1);
    },
    [activeIndex, activeSequence, bloc, formValue.sequence, lessonId, props.editable, props.preview, sequence.length, isDrillCompleted],
  );

  useConfigureBlock({
    props,
    form,
    contentComponent: SequenceDrillBlockSettings,
    contentProps: { form, variation },
  });

  const onSubmit = useCallback(
    () => of(isDrillCompleted && !wrongAnswer),
    [wrongAnswer, isDrillCompleted],
  );

  useCourseForm(props.id, form, { onSubmit });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-sequence-drill', variation)} advanceForm={formValue.advance}>
        <div style={{ flex: 1 }}>
          <div>
            {!!activeSequence && !isDrillCompleted && (
              <SequenceItem
                form={activeSequence}
                onNext={onNext}
                index={activeIndex + 1}
                totalQuestions={formValue.sequence.length}
                lessonId={lessonId || ''}
                isDrillCompleted={isDrillCompleted}
                editable={editable}
              />
            )}
            {isDrillCompleted && (
              <div className="d-flex align-items-start mt-2 flex-column">
                <span className="text-success mb-2">Congratulations! You have completed this drill successfully.</span>
                <Button type="button" onClick={onResetDrill}>
                  Redo Drill
                </Button>
              </div>
            )}
            {wrongAnswer && (
              <div className="d-flex align-items-start mt-2 flex-column">
                <span className="text-danger mb-2">You have to get 100% correct answers to pass this drill.</span>
              </div>
            )}
          </div>
        </div>
      </BlockContainer>
    </Container>
  );
});

interface SequenceItemProps {
  form: SequenceForm;
  onNext: (val: boolean) => void;
  totalQuestions: number;
  index: number;
  lessonId: string;
  isDrillCompleted: boolean;
  editable: boolean;
}

const SequenceItem = memo((props: SequenceItemProps) => {
  const { form, onNext, index, lessonId, isDrillCompleted, editable } = props;
  const formValue = useNonNilObservable(form.value$);

  return (
    <div className="mb-2 ml-4 mt-4">
      <div className="pb-4">
        <span>{HTMLService.toReact(formValue.description)}</span>
      </div>
      <SequenceDrillItem control={form} onNext={onNext} index={index} lessonId={lessonId} isDrillCompleted={props.isDrillCompleted} editable={editable} />
    </div>
  );
});

export interface SequenceDrillItemProps {
  control: SequenceForm;
  onNext: (val: boolean) => void;
  index: number;
  lessonId: string;
  isDrillCompleted: boolean;
  editable: boolean;
}

export const SequenceDrillItem = memo((props: SequenceDrillItemProps) => {
  const { control, index, lessonId, isDrillCompleted, editable } = props;
  const itemValues = useObservable(control.controls.items.value$, []);
  const [sequenceItems, setSequenceItems] = useState<any[]>([]);
  const bloc = useBloc(SequenceDrillBlockBloc, lessonId);

  useEffect(() => {
    setSequenceItems(R.shuffle(itemValues));
  }, [itemValues, index, control]);

  useEffect(() => {
    if (isDrillCompleted) {
      setSequenceItems(R.shuffle(itemValues));
    }
  }, [props.isDrillCompleted, isDrillCompleted]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newSequence = R.move(sequenceItems, oldIndex, newIndex);
      setSequenceItems(newSequence);
    },
    [control, sequenceItems],
  );

  const onNext = useCallback(() => {
    let result = sequenceItems.length === itemValues.length && sequenceItems.every((obj, index) => obj.item === itemValues[index].item);
    props.onNext(result);

    if (editable || R.isEmpty(lessonId)) return;
    bloc.onProgress(lessonId || '', {
      title: control.controls.description.value,
      answer: JSON.stringify(sequenceItems),
      correct: JSON.stringify(itemValues),
      status: result ? 'pass' : 'fail',
    }).subscribe();
  }, [control, props, sequenceItems, itemValues, editable]);

  return (
    <div>
      <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={false} helperClass="z5000">
        {sequenceItems.map((itemValue, index) => {
          return <SortableSequenceItem key={index} index={index} i={index} item={itemValue} />;
        })}
      </SortableList>
      <div className="d-flex align-items-start flex-column mt-2">
        <Button type="button" onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
});

interface SequenceItemListProps {
  i: number;
  item: SequenceItemOptions;
}

export interface SequenceItemOptions {
  item: string;
}

const SequenceItemList = memo((props: SequenceItemListProps) => {
  const { item, i } = props;

  return (
    <Row key={`item-${i}`} className="sequence-course-radio-button">
      <Col md={6} className="d-flex inline sequence-item-div">
        <p className='sequence-item'>{item.item}</p>
      </Col>
    </Row>
  );
});

export const SortableSequenceItem = SortableElement(SequenceItemList);
